import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Popup, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '@/services/api';
import { REFETCH_GPS_MAP_MS } from '@/shared/timing';
import { isInNativeWrapper, dialPhone } from '@/services/nativeBridge';
import type { ResponsesData } from './types';

/**
 * Apre Google Maps con la rotta dalla posizione CORRENTE (origin omesso ->
 * GMaps usa il GPS del device) alla destinazione del marker cliccato.
 * Browser puro: nuova scheda. Wrapper RN: la navigazione top-level e'
 * intercettata da onShouldStartLoadWithRequest (M4) e aperta nell'app Google
 * Maps / browser di sistema, lasciando la PWA intatta.
 */
function openGoogleMapsDirections(lat: number, lng: number): void {
  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  if (isInNativeWrapper()) {
    window.location.assign(url);
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

const NAV_BTN_STYLE: React.CSSProperties = {
  marginTop: 8, width: '100%', padding: '6px 8px', fontSize: 12, fontWeight: 600,
  border: 'none', borderRadius: 6, cursor: 'pointer', background: '#1a73e8', color: '#fff',
};

/**
 * Fix2: react-leaflet applica la prop `center` di MapContainer SOLO al mount.
 * Le coordinate del distaccamento arrivano async (query /admin/my-tenant), e
 * anche le posizioni utenti cambiano nel tempo: questo helper ri-centra la
 * mappa via map.setView ogni volta che `center` cambia. (Gate del render a
 * parte, vedi sotto, assicura che al primo mount il center sia gia' corretto.)
 */
function RecenterMap({ center }: { center: [number, number] }): null {
  const map = useMap();
  useEffect(() => { map.setView(center); }, [center, map]);
  return null;
}

/**
 * Mappa Leaflet con posizioni in tempo reale degli utenti che hanno
 * accettato la selettiva + marker distaccamento + ETA percorso stradale
 * via OSRM (fallback haversine se non risponde).
 */
export function SelectiveGpsMapModal({ selectiveId, onClose }: { selectiveId: string; onClose: () => void }): React.JSX.Element {
  const { data } = useQuery<ResponsesData>({
    queryKey: ['selective.responses', selectiveId],
    queryFn: async () => (await api.get(`/selectives/${selectiveId}/responses`)).data,
    refetchInterval: REFETCH_GPS_MAP_MS,
  });
  // Coordinate del distaccamento per ETA e marker mappa
  const { data: myTenant, status: tenantStatus } = useQuery<{ latitude: number; longitude: number; name: string }>({
    queryKey: ['admin.my-tenant'],
    queryFn: async () => (await api.get('/admin/my-tenant')).data,
  });
  // Fix1: la mappa va montata SOLO quando la query distaccamento e' conclusa
  // (success o error), cosi' al primo mount il `center` e' gia' quello del
  // distaccamento (react-leaflet ignora i cambi di center dopo il mount).
  const tenantReady = tenantStatus !== 'pending';
  const tenantCoords = myTenant && myTenant.latitude && myTenant.longitude
    ? { lat: myTenant.latitude, lng: myTenant.longitude } : null;

  const accepted = (data?.accepted ?? []).filter(u => u.latitude && u.longitude);
  // Centro mappa: distaccamento se disponibile, altrimenti media utenti, altrimenti centro Italia
  const center: [number, number] = tenantCoords
    ? [tenantCoords.lat, tenantCoords.lng]
    : accepted.length > 0
      ? [
          accepted.reduce((s, u) => s + (u.latitude ?? 0), 0) / accepted.length,
          accepted.reduce((s, u) => s + (u.longitude ?? 0), 0) / accepted.length,
        ]
      : [42.5, 12.5];

  // ETA reale via OSRM (percorso stradale). Fetch batch: distaccamento ->
  // ogni pompiere accettato. Risultato minimo = ETA del piu' vicino su strada.
  // Fallback haversine (linea d'aria + 50km/h) se OSRM non risponde.
  const { data: etaBatch } = useQuery({
    queryKey: ['selective.eta', selectiveId, tenantCoords?.lat, tenantCoords?.lng, accepted.length],
    queryFn: async () => {
      if (!tenantCoords || accepted.length === 0) return [];
      const body = {
        fromLat: tenantCoords.lat,
        fromLng: tenantCoords.lng,
        to: accepted
          .filter(u => u.latitude != null && u.longitude != null)
          .map(u => ({ id: u.userId, lat: u.latitude!, lng: u.longitude! })),
      };
      if (body.to.length === 0) return [];
      const r = await api.post('/routing/eta-batch', body);
      return r.data as Array<{ id: string; distanceKm: number | null; durationMinutes: number | null }>;
    },
    enabled: !!tenantCoords && accepted.length > 0,
    staleTime: 30_000,
  });
  function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const toRad = (d: number) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  const etaMinutes = (() => {
    if (!tenantCoords || accepted.length === 0) return null;
    const osrmMin = etaBatch
      ?.map(e => e.durationMinutes)
      .filter((d): d is number => d != null);
    if (osrmMin && osrmMin.length > 0) return Math.round(Math.min(...osrmMin));
    // Fallback haversine
    return Math.round(Math.min(...accepted.map(u =>
      haversineKm(tenantCoords.lat, tenantCoords.lng, u.latitude!, u.longitude!),
    )) / 50 * 60);
  })();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
        <h3 style={{ marginBottom: 10 }}>📍 Posizioni Utenti Selettiva</h3>
        {etaMinutes !== null && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', padding: 8, borderRadius: 6, marginBottom: 10, fontSize: 14 }}>
            🚒 <strong>ETA percorso stradale</strong> dal distaccamento al pompiere più vicino: <strong>~{etaMinutes} min</strong>
            {!etaBatch && <span style={{ marginLeft: 6, fontSize: 11, color: '#856404' }}>(calcolo in corso…)</span>}
          </div>
        )}
        <div style={{ fontSize: 13, marginBottom: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#28a745', borderRadius: '50%', marginRight: 5 }} /> Hanno accettato (GPS attivo)</span>
          {tenantCoords && <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#dc3545', borderRadius: '50%', marginRight: 5 }} /> Distaccamento</span>}
        </div>
        <div style={{ height: 350, borderRadius: 8, overflow: 'hidden', border: '2px solid #ddd', marginBottom: 12 }}>
          {!tenantReady ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 14 }}>
              Caricamento mappa…
            </div>
          ) : (
          <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
            <RecenterMap center={center} />
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
            {tenantCoords && (
              <CircleMarker center={[tenantCoords.lat, tenantCoords.lng]}
                radius={14} pathOptions={{ color: '#dc3545', fillColor: '#dc3545', fillOpacity: 0.9 }}>
                <Popup>
                  <strong>🚒 {myTenant?.name}</strong><br />Distaccamento
                  <button style={NAV_BTN_STYLE}
                    onClick={() => openGoogleMapsDirections(tenantCoords.lat, tenantCoords.lng)}>
                    🧭 Naviga con Google Maps
                  </button>
                </Popup>
              </CircleMarker>
            )}
            {accepted.map(u => (
              <CircleMarker key={u.userId} center={[u.latitude!, u.longitude!]}
                radius={10} pathOptions={{ color: '#28a745', fillColor: '#28a745', fillOpacity: 0.8 }}>
                <Popup>
                  <strong>{u.nickname}</strong><br />
                  {u.phoneNumber
                    ? <a href={`tel:${u.phoneNumber}`}
                         onClick={e => { e.preventDefault(); dialPhone(u.phoneNumber ?? ''); }}
                         style={{ color: '#0056b3', fontWeight: 600 }}>📞 {u.phoneNumber}</a>
                    : u.email}<br />
                  <small>Ultimo aggiornamento: {new Date(u.respondedAt).toLocaleTimeString('it-IT')}</small>
                  <button style={NAV_BTN_STYLE}
                    onClick={() => openGoogleMapsDirections(u.latitude!, u.longitude!)}>
                    🧭 Naviga con Google Maps
                  </button>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
          )}
        </div>
        <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 13, marginBottom: 10 }}>
          {accepted.length === 0 && <p style={{ color: '#999', textAlign: 'center' }}>Nessun utente ha ancora attivato il GPS</p>}
          {/* Bryan: sotto la mappa lista utenti con il loro ETA (non le coordinate).
              etaBatch viene dal POST /routing/eta-batch (OSRM percorso stradale).
              Se non ancora calcolato o utente non ha GPS, mostra placeholder. */}
          {(() => {
            const etaById = new Map<string, number | null>(
              (etaBatch ?? []).map(e => [e.id, e.durationMinutes]),
            );
            return accepted.map(u => {
              const mins = etaById.get(u.userId);
              return (
                <div key={u.userId} style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <strong>{u.nickname}</strong>
                  <span style={{ color: '#0056b3', fontWeight: 600 }}>
                    {mins != null ? `🚒 ~${Math.round(mins)} min` : <span style={{ color: '#999', fontWeight: 400 }}>ETA in calcolo…</span>}
                  </span>
                </div>
              );
            });
          })()}
        </div>
        <button onClick={onClose}
          style={{ width: '100%', padding: 12, fontSize: 16, fontWeight: 'bold',
                   border: 'none', borderRadius: 8, cursor: 'pointer',
                   background: '#6c757d', color: 'white' }}>
          Chiudi
        </button>
      </div>
    </div>
  );
}
